'use client';

import React from 'react';

interface AISourceIndicatorProps {
  source: 'you' | 'cognition' | undefined;
  children: React.ReactNode;
  className?: string;
}

/**
 * AI Source Identification System
 *
 * Simplified visual language:
 * - "Your AI" (from: you): Purple/pink gradient edge accent
 * - "Business AI" (from: cognition): Blue/cyan gradient edge accent
 *
 * Combined with text attribution in expanded views for clarity
 * Mobile-first: Works consistently across all devices
 */
export const AISourceIndicator: React.FC<AISourceIndicatorProps> = ({ source, children, className = '' }) => {
  // If no source specified, render children without indicator
  if (!source) {
    return <>{children}</>;
  }

  return (
    <div className={`relative ${className}`}>
      {/* Border treatment - edge accent only */}
      <div
        className={`
          relative overflow-hidden rounded-xl
          transition-all duration-200
          active:scale-[0.99]
        `}
      >
        {/* Left edge accent - 4px colored border for better visibility */}
        <div
          className={`
            absolute top-0 left-0 bottom-0 w-1
            ${
              source === 'you'
                ? 'bg-gradient-to-b from-purple-500 to-pink-500'
                : 'bg-gradient-to-b from-blue-500 to-cyan-500'
            }
          `}
        />

        {children}
      </div>
    </div>
  );
};

/**
 * Minimal variant for small cards
 */
export const AISourceIndicatorMinimal: React.FC<AISourceIndicatorProps> = ({
  source,
  children,
  className = '',
}) => {
  if (!source) {
    return <>{children}</>;
  }

  return (
    <div className={`relative ${className}`}>
      {/* Subtle left edge indicator only */}
      <div className="relative overflow-hidden">
        <div
          className={`
            absolute top-0 left-0 bottom-0 w-1
            ${
              source === 'you'
                ? 'bg-gradient-to-b from-purple-500 to-pink-500'
                : 'bg-gradient-to-b from-blue-500 to-cyan-500'
            }
          `}
        />
        {children}
      </div>
    </div>
  );
};

/**
 * Hook for programmatic access to source colors
 */
export const useAISourceTheme = (source: 'you' | 'cognition' | undefined) => {
  if (!source) return null;

  return source === 'you'
    ? {
        primary: 'purple',
        secondary: 'pink',
        gradient: 'from-purple-500 to-pink-500',
        ring: 'ring-purple-200 dark:ring-purple-800/50',
        text: 'text-purple-600 dark:text-purple-400',
      }
    : {
        primary: 'blue',
        secondary: 'cyan',
        gradient: 'from-blue-500 to-cyan-500',
        ring: 'ring-blue-200 dark:ring-blue-800/50',
        text: 'text-blue-600 dark:text-blue-400',
      };
};

export default AISourceIndicator;
